import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  ArrowLeft, Download, Upload, Link as LinkIcon, FileText, 
  MessageSquare, CheckCircle2, Clock, AlertCircle, Calendar,
  Car, Gauge, Fuel, Hash, Edit2, Check, X, LifeBuoy
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import ChatBox from '../Chat/ChatBox';
import { parseUTC } from '../../lib/date';

const OrderDetail = ({ user }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [diagnosticLink, setDiagnosticLink] = useState('');
  const [diagnosticFile, setDiagnosticFile] = useState(null);
  const [uploadingLog, setUploadingLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editingLogName, setEditingLogName] = useState('');
  const [savOpening, setSavOpening] = useState(false);

  const handleOpenSAV = async () => {
    const message = window.prompt(
      "Décrivez votre demande de SAV (problème rencontré, ajustement souhaité...)",
      ""
    );
    if (message === null) return;
    setSavOpening(true);
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.openSAV(order.order_id, message);
      toast({
        title: "Demande de SAV envoyée ✓",
        description: "Un ingénieur va vous répondre dans les plus brefs délais."
      });
      // Reload order to show SAV state
      loadOrder();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'ouvrir le SAV",
        variant: "destructive"
      });
    } finally {
      setSavOpening(false);
    }
  };
  const [services, setServices] = useState([]);
  const [optionsData, setOptionsData] = useState([]);

  useEffect(() => {
    loadOrderDetails();
    loadLogs();
    loadServicesAndOptions();
  }, [orderId]);

  const loadServicesAndOptions = async () => {
    try {
      const [servicesRes, optionsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/services`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/options`)
      ]);
      const servicesData = await servicesRes.json();
      const optionsDataRes = await optionsRes.json();
      setServices(servicesData);
      setOptionsData(optionsDataRes);
    } catch (error) {
      console.error('Error loading services/options:', error);
    }
  };

  const loadOrderDetails = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const data = await ordersAPI.getOrder(orderId);
      setOrder(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les détails de la commande",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const logsData = await ordersAPI.getOrderLogs(orderId);
      setLogs(logsData);
    } catch (error) {
      console.error('Error loading logs:', error);
      // Ne pas afficher d'erreur si les logs n'existent pas encore
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDiagnosticFile(file);
    toast({
      title: "Fichier sélectionné",
      description: `${file.name} prêt à être uploadé`,
    });
  };

  const handleAddLog = async () => {
    if (!diagnosticLink.trim() && !diagnosticFile) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un fichier ou entrer un lien",
        variant: "destructive"
      });
      return;
    }

    setUploadingLog(true);

    try {
      const { ordersAPI } = await import('../../api');
      
      if (diagnosticFile) {
        // Upload file
        await ordersAPI.uploadLog(orderId, diagnosticFile);
        toast({
          title: "Log ajouté",
          description: "Le fichier de diagnostic a été uploadé",
        });
      } else if (diagnosticLink.trim()) {
        // Add link
        await ordersAPI.addLogLink(orderId, diagnosticLink);
        toast({
          title: "Lien ajouté",
          description: "Le lien de diagnostic a été ajouté",
        });
      }

      // Reset and reload
      setDiagnosticLink('');
      setDiagnosticFile(null);
      await loadLogs();
      
    } catch (error) {
      console.error('Error adding log:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'ajouter le log",
        variant: "destructive"
      });
    } finally {
      setUploadingLog(false);
    }
  };

  const handleDownloadLog = async (logId, fileName) => {
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.downloadLog(orderId, logId);
      toast({
        title: "Téléchargement démarré",
        description: `${fileName} est en cours de téléchargement`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const handleRenameLog = async (logId, newName) => {
    if (!newName.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom ne peut pas être vide",
        variant: "destructive"
      });
      return;
    }

    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.renameLog(orderId, logId, newName);
      toast({
        title: "Succès",
        description: "Le log a été renommé",
      });
      setEditingLogId(null);
      setEditingLogName('');
      await loadLogs();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de renommer le log",
        variant: "destructive"
      });
    }
  };

  const handleDownloadFile = async (fileType) => {
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.downloadFile(orderId, fileType);
      toast({
        title: "Téléchargement démarré",
        description: `Le fichier ${fileType} est en cours de téléchargement`,
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const handleDownloadModifiedFile = async (fileId, filename) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/download-by-id/${fileId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to download file');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Succès",
        description: `${filename} téléchargé avec succès`,
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const handleAddDiagnosticLink = () => {
    if (!diagnosticLink.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer un lien valide",
        variant: "destructive"
      });
      return;
    }

    handleAddLog();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-lg px-4 py-2">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Terminé
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30 text-lg px-4 py-2">
            <Clock className="h-4 w-4 mr-2" />
            En cours
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 text-lg px-4 py-2">
            <AlertCircle className="h-4 w-4 mr-2" />
            En attente
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30 text-lg px-4 py-2">
            {status}
          </Badge>
        );
    }
  };

  const getServiceName = (serviceKey) => {
    const service = services.find(s => s.key === serviceKey);
    return service ? service.name : serviceKey;
  };

  const getOptionName = (optionKey) => {
    const option = optionsData.find(o => o.key === optionKey);
    return option ? option.name : optionKey;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <ClientSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="text-center py-12">
            <p className="text-[#A6A6A6]">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <ClientSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="text-center py-12">
            <p className="text-[#A6A6A6]">Commande non trouvée</p>
            <Button onClick={() => navigate('/client/orders')} className="mt-4 bg-[#C00000]">
              Retour aux commandes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Button
            onClick={() => navigate('/client/orders')}
            variant="outline"
            className="mb-6 border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux demandes
          </Button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  {order.vehicle_info.brand.toUpperCase()} {order.vehicle_info.model.toUpperCase()}
                </h1>
                <p className="text-[#A6A6A6] text-lg">
                  {order.vehicle_info.year} • {order.vehicle_info.engine}
                </p>
              </div>
              <div className="flex flex-col items-end gap-3">
                {getStatusBadge(order.status)}
                {/* Ouvrir un SAV — disponible une fois la commande terminée */}
                {order.status === 'completed' && (
                  <Button
                    onClick={handleOpenSAV}
                    disabled={savOpening}
                    data-testid="open-sav-btn"
                    variant="outline"
                    className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                  >
                    <LifeBuoy className="h-4 w-4 mr-2" />
                    {savOpening ? 'Envoi...' : (order.sav_open ? 'SAV ouvert' : 'Ouvrir un SAV')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Options de Tuning */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-[#C00000]" />
                    OPTIONS DE TUNING
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-yellow-600/20 text-yellow-500 border-yellow-600/30 px-4 py-2 text-sm">
                      {getServiceName(order.service)}
                    </Badge>
                    {order.options && order.options.length > 0 && order.options.map((opt, index) => (
                      <Badge key={index} className="bg-yellow-600/20 text-yellow-500 border-yellow-600/30 px-4 py-2 text-sm">
                        {getOptionName(opt)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Liens Log / Diagnostic */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-white flex items-center gap-2">
                      <LinkIcon className="h-5 w-5 text-[#C00000]" />
                      LIENS LOG
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Upload File */}
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6] text-sm">Fichier de diagnostic</Label>
                      <div className="flex gap-3">
                        <Input
                          type="file"
                          onChange={handleFileSelect}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                          accept=".log,.txt,.csv,.pdf"
                        />
                        <Upload className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-2" />
                      </div>
                      {diagnosticFile && (
                        <p className="text-xs text-green-500">✓ {diagnosticFile.name}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="h-px flex-1 bg-[#A6A6A6]/20"></div>
                      <span className="text-[#A6A6A6] text-sm">OU</span>
                      <div className="h-px flex-1 bg-[#A6A6A6]/20"></div>
                    </div>

                    {/* Add Link */}
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6] text-sm">Lien vers le diagnostic</Label>
                      <div className="flex gap-3">
                        <Input
                          value={diagnosticLink}
                          onChange={(e) => setDiagnosticLink(e.target.value)}
                          placeholder="https://..."
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleAddLog}
                      disabled={uploadingLog || (!diagnosticFile && !diagnosticLink.trim())}
                      className="w-full bg-[#C00000] hover:bg-[#C00000]/90 disabled:opacity-50"
                    >
                      {uploadingLog ? 'Ajout en cours...' : '+ Ajouter'}
                    </Button>

                    {/* List of logs */}
                    {logs.length > 0 ? (
                      <div className="space-y-2 pt-4 border-t border-[#A6A6A6]/20">
                        {logs.map((log, index) => (
                          <div key={log.id || index} className="flex justify-between items-center p-3 bg-[#111111] rounded-lg">
                            <div className="flex items-center gap-3 flex-1">
                              {log.type === 'file' ? (
                                <FileText className="h-4 w-4 text-[#C00000] flex-shrink-0" />
                              ) : (
                                <LinkIcon className="h-4 w-4 text-[#C00000] flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                {editingLogId === log.id ? (
                                  <div className="flex gap-2 items-center">
                                    <Input
                                      value={editingLogName}
                                      onChange={(e) => setEditingLogName(e.target.value)}
                                      className="bg-[#0a0a0a] border-[#C00000] text-white h-8 text-sm"
                                      placeholder={`V${index + 1}`}
                                      autoFocus
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleRenameLog(log.id, editingLogName)}
                                      className="bg-green-500 hover:bg-green-600 h-8 px-2"
                                    >
                                      <Check className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setEditingLogId(null);
                                        setEditingLogName('');
                                      }}
                                      variant="outline"
                                      className="border-[#A6A6A6] text-white h-8 px-2"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-white font-semibold">
                                      {log.custom_name || `V${index + 1}`}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingLogId(log.id);
                                        setEditingLogName(log.custom_name || `V${index + 1}`);
                                      }}
                                      className="h-6 w-6 p-0 text-[#A6A6A6] hover:text-white hover:bg-[#A6A6A6]/10"
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                                <p className="text-[#A6A6A6] text-xs">
                                  {log.type === 'file' ? log.filename : 'Lien externe'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {log.type === 'file' ? (
                                <Button
                                  onClick={() => handleDownloadLog(log.id, log.filename)}
                                  size="sm"
                                  variant="outline"
                                  className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => window.open(log.url, '_blank')}
                                  size="sm"
                                  variant="outline"
                                  className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                                >
                                  Ouvrir
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#A6A6A6] text-sm text-center py-2">Aucun log ajouté.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fichiers Originaux */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Upload className="h-5 w-5 text-[#C00000]" />
                    FICHIER ORIGINAL
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {order.original_file_name ? (
                      <div className="flex justify-between items-center p-3 bg-[#111111] rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-[#C00000]" />
                          <span className="text-white">Fichier original uploadé</span>
                        </div>
                        <Button
                          onClick={() => handleDownloadFile('original')}
                          size="sm"
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Télécharger
                        </Button>
                      </div>
                    ) : (
                      <p className="text-[#A6A6A6] text-sm text-center py-2">Aucun fichier original uploadé.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fichiers Modifiés (Multi-versions) */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Download className="h-5 w-5 text-green-500" />
                    FICHIERS MODIFIÉS
                  </CardTitle>
                  <p className="text-[#A6A6A6] text-sm">Téléchargez les versions de votre fichier optimisé</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {order.modified_files && order.modified_files.length > 0 ? (
                      order.modified_files.map((modFile, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-green-500" />
                            <div>
                              <span className="text-white font-semibold block">{modFile.version}</span>
                              <span className="text-[#A6A6A6] text-xs">{modFile.filename}</span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleDownloadModifiedFile(modFile.file_id, modFile.filename)}
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            {modFile.version}
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="h-12 w-12 text-[#A6A6A6] mx-auto mb-3" />
                        <p className="text-[#A6A6A6] text-sm">Fichier modifié en cours de préparation</p>
                        <p className="text-[#A6A6A6] text-xs mt-1">Vous serez notifié dès qu'il sera disponible</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Chat et Infos */}
            <div className="space-y-6">
              {/* Chat SAV - Visible par défaut */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[#C00000]" />
                    CHAT SAV
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[400px]">
                    <ChatBox 
                      orderId={order.order_id}
                      userRole="client"
                      onClose={() => {}} 
                      embedded={true}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Détails Véhicule (regroupe l'ancienne carte Information) */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Car className="h-5 w-5 text-[#C00000]" />
                      DÉTAILS VÉHICULE
                    </CardTitle>
                    <span className="text-[#A6A6A6] text-sm">
                      {parseUTC(order.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Infos commande */}
                  <div className="pb-3 border-b border-[#A6A6A6]/20 space-y-3">
                    <div>
                      <Label className="text-[#A6A6A6] text-xs">Numéro de commande</Label>
                      <p className="text-white font-mono">{order.order_id}</p>
                    </div>
                    <div>
                      <Label className="text-[#A6A6A6] text-xs">Statut</Label>
                      <div className="mt-1">{getStatusBadge(order.status)}</div>
                    </div>
                    <div className="bg-[#C00000]/10 border border-[#C00000]/30 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A6A6A6]">Prix</span>
                        <span className="text-2xl font-bold text-[#C00000]">{order.price}€</span>
                      </div>
                    </div>
                  </div>

                  {/* Détails véhicule */}
                  {order.vehicle_info.category && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Catégorie</span>
                      <span className="text-white font-semibold">{order.vehicle_info.category}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Marque</span>
                    <span className="text-white font-semibold">{order.vehicle_info.brand}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Modèle</span>
                    <span className="text-white font-semibold">{order.vehicle_info.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Année</span>
                    <span className="text-white font-semibold">{order.vehicle_info.year}</span>
                  </div>
                  {order.vehicle_info.mileage && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Kilométrage</span>
                      <span className="text-white font-semibold">{order.vehicle_info.mileage}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#A6A6A6]">Moteur</span>
                    <span className="text-white font-semibold">{order.vehicle_info.engine}</span>
                  </div>
                  {order.vehicle_info.power && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Puissance</span>
                      <span className="text-white font-semibold">{order.vehicle_info.power} CV</span>
                    </div>
                  )}
                  {order.vehicle_info.fuel && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Carburant</span>
                      <span className="text-white font-semibold">{order.vehicle_info.fuel}</span>
                    </div>
                  )}
                  {order.vehicle_info.gearbox && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Boîte</span>
                      <span className="text-white font-semibold">{order.vehicle_info.gearbox}</span>
                    </div>
                  )}
                  {order.vehicle_info.transmission && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Transmission</span>
                      <span className="text-white font-semibold">{order.vehicle_info.transmission}</span>
                    </div>
                  )}
                  {order.vehicle_info.ecu_type && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Type ECU</span>
                      <span className="text-white font-semibold">{order.vehicle_info.ecu_type}</span>
                    </div>
                  )}
                  {order.vehicle_info.ecu_tool && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Outil ECU</span>
                      <span className="text-white font-semibold">{order.vehicle_info.ecu_tool}</span>
                    </div>
                  )}
                  {order.vehicle_info.file_type && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Type fichier</span>
                      <span className="text-white font-semibold">{order.vehicle_info.file_type}</span>
                    </div>
                  )}
                  {order.vehicle_info.read_method && (
                    <div className="flex justify-between">
                      <span className="text-[#A6A6A6]">Méthode lecture</span>
                      <span className="text-white font-semibold">{order.vehicle_info.read_method}</span>
                    </div>
                  )}
                  {order.vehicle_info.hardware_mods && order.vehicle_info.hardware_mods.length > 0 && (
                    <div className="pt-3 border-t border-[#A6A6A6]/20">
                      <Label className="text-[#A6A6A6] text-xs mb-2 block">MODIFICATIONS HARDWARE</Label>
                      <div className="flex flex-wrap gap-2">
                        {order.vehicle_info.hardware_mods.map((mod, i) => (
                          <Badge key={i} variant="outline" className="border-[#C00000]/50 text-white text-xs">
                            {mod}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {order.notes && (
                    <div className="pt-3 border-t border-[#A6A6A6]/20">
                      <Label className="text-[#A6A6A6] text-xs">Notes</Label>
                      <p className="text-white text-sm mt-1">{order.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
